import { supabase } from '@/lib/supabase';

export interface MarketSummary {
  id: string;
  symbol: string;
  timeframe: string;
  dominant_sentiment: string;
  percent_long: number;
  percent_short: number;
  percent_neutral: number;
  confidence_score: number;
  signals_analysis: any;
  sentiment_data: any;
  support_levels: any[];
  resistance_levels: any[];
  market_condition: string;
  created_at: string;
  updated_at: string;
  // Additional fields from database schema
  total_summary?: string;
  num_long?: number;
  num_short?: number;
  num_neutral?: number;
  total_ideas?: number;
  top_keywords?: string[];
  analysis_period_start?: string;
  analysis_period_end?: string;
  trend_confluence?: string;
  technical_confidence_score?: number;
  combined_confidence_score?: number;
  trading_recommendations?: any;
  market_alerts?: any;
}

export interface CreateMarketSummaryData {
  symbol: string;
  timeframe: string;
  signals_analysis: any;
  sentiment_data: any;
  support_levels: any[];
  resistance_levels: any[];
  market_condition: string;
}

export interface AnalysisRequest {
  symbol: string;
  limit: number;
  startDate: string;
  endDate: string;
}

export class MarketSummaryService {
  /**
   * Get market summaries with optional symbol filter
   */
  static async getMarketSummaries(limit: number = 20, symbol?: string): Promise<MarketSummary[]> {
    try {
      let query = supabase
        .from('tradingview_market_summary')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (symbol) {
        query = query.eq('symbol', symbol);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching market summaries:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getMarketSummaries:', error);
      return [];
    }
  }

  /**
   * Create a new market summary
   */
  static async createMarketSummary(summaryData: CreateMarketSummaryData): Promise<{ success: boolean; data?: MarketSummary; error?: string }> {
    try {
      // Calculate sentiment percentages from sentiment_data
      const overallSentiment = summaryData.sentiment_data?.overall_sentiment || 'neutral';
      let percentLong = 33;
      let percentShort = 33;
      let percentNeutral = 34;

      if (overallSentiment === 'positive') {
        percentLong = 60;
        percentShort = 20;
        percentNeutral = 20;
      } else if (overallSentiment === 'negative') {
        percentLong = 20;
        percentShort = 60;
        percentNeutral = 20;
      }

      const confidenceScore = Math.round(
        ((summaryData.sentiment_data?.news_sentiment || 0.5) + 
         (summaryData.sentiment_data?.social_sentiment || 0.5)) * 50
      );

      const { data, error } = await supabase
        .from('tradingview_market_summary')
        .insert({
          symbol: summaryData.symbol,
          timeframe: summaryData.timeframe,
          dominant_sentiment: overallSentiment,
          percent_long: percentLong,
          percent_short: percentShort,
          percent_neutral: percentNeutral,
          confidence_score: confidenceScore,
          signals_analysis: summaryData.signals_analysis,
          sentiment_data: summaryData.sentiment_data,
          support_levels: summaryData.support_levels,
          resistance_levels: summaryData.resistance_levels,
          market_condition: summaryData.market_condition
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating market summary:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in createMarketSummary:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Delete market summary
   */
  static async deleteMarketSummary(id: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('tradingview_market_summary')
        .delete()
        .eq('id', id);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in deleteMarketSummary:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Get market summary by ID
   */
  static async getMarketSummaryById(id: string): Promise<MarketSummary | null> {
    try {
      const { data, error } = await supabase
        .from('tradingview_market_summary')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching market summary by ID:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in getMarketSummaryById:', error);
      return null;
    }
  }

  /**
   * Get available symbols from the database
   */
  static async getAvailableSymbols(): Promise<string[]> {
    try {
      const { data, error } = await supabase
        .from('tradingview_market_summary')
        .select('symbol')
        .order('symbol');

      if (error) {
        console.error('Error fetching available symbols:', error);
        return [];
      }

      // Get unique symbols
      const uniqueSymbols = [...new Set(data?.map(item => item.symbol) || [])];
      return uniqueSymbols;
    } catch (error) {
      console.error('Error in getAvailableSymbols:', error);
      return [];
    }
  }

  /**
   * Analyze market ideas using database function
   */
  static async analyzeMarketIdeas(request: AnalysisRequest): Promise<{ success: boolean; summaryId?: string; error?: string }> {
    try {
      // Validate input parameters
      if (!request.symbol?.trim()) {
        return { success: false, error: 'Symbol is required' };
      }

      if (request.limit < 1 || request.limit > 1000) {
        return { success: false, error: 'Limit must be between 1 and 1000' };
      }

      const startDate = new Date(request.startDate);
      const endDate = new Date(request.endDate);
      
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return { success: false, error: 'Invalid date format' };
      }

      if (startDate >= endDate) {
        return { success: false, error: 'Start date must be before end date' };
      }

      console.log('Calling analyze_market_ideas with params:', {
        symbol: request.symbol.trim().toUpperCase(),
        limit: request.limit,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString()
      });

      // Call the database function via Supabase RPC
      const { data: summaryId, error } = await supabase.rpc('analyze_market_ideas', {
        p_symbol: request.symbol.trim().toUpperCase(),
        p_limit: request.limit,
        p_start_date: startDate.toISOString(),
        p_end_date: endDate.toISOString()
      });

      if (error) {
        console.error('Database function error:', error);
        return { 
          success: false, 
          error: `Analysis failed: ${error.message}` 
        };
      }

      if (!summaryId) {
        return { 
          success: false, 
          error: 'Analysis completed but no summary ID was returned' 
        };
      }

      console.log('Analysis completed successfully, summary ID:', summaryId);

      return { 
        success: true, 
        summaryId: summaryId 
      };

    } catch (error) {
      console.error('Error in analyzeMarketIdeas:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred during analysis' 
      };
    }
  }
}
